/**
 * Journal posts — the editorial heart of Authentic Momentum.
 *
 * Each post is a fully self-contained record holding everything the
 * index page, individual post page, and SEO/Schema injectors need.
 * Body content is structured into sections (a heading + paragraphs)
 * rather than raw HTML so the renderer can apply consistent typography
 * and the same "living motion" reveals used elsewhere on the site.
 *
 * Imagery: each post has a `featuredImage.prompt` describing the
 * cinematic editorial still life that should be generated (Nano Banana
 * etc). Until those exist on disk, the renderer falls back to a
 * brand-native poster card built from `featuredImage.motif` + the
 * post's theme tags — a black/charcoal gradient with a single gold
 * SVG glyph. This matches the brand far better than stock photography.
 *
 * Volume II (May 2026): ten new posts were added at the top of the
 * list to break the rhythm of Volume I. They live in `journalPostsV2`
 * and are prepended so the newest material renders first on /journal.
 */

import JOURNAL_POSTS_V2 from "./journalPostsV2";

const PUBLISHED_AT = "2026-02-08";
const UPDATED_AT = "2026-02-08";

export const ALL_THEMES = [
  "Clarity",
  "Energy",
  "Identity",
  "Resistance",
  "Alignment",
  "Momentum",
  "Signal",
  "Strategy",
  "Focus",
  "Boundaries",
  "Direction",
  "Stillness",
  "Confidence",
  "Self-Trust",
  "Accountability",
  "Timing",
  "Fear",
  "Authenticity",
  // Volume II diversity categories.
  "Transition",
  "Healing",
  "Relationships",
  "Reflection",
  "Purpose",
  "Inner Work",
  "Discipline",
];

export const JOURNAL_POSTS = [
  ...JOURNAL_POSTS_V2,
  {
    id: 1,
    slug: "not-stuck-loyal-to-old-version",
    title: "You Are Not Stuck. You Are Loyal to an Old Version of Yourself.",
    excerpt:
      "Feeling stuck may not mean you lack clarity. You may be protecting an older version of yourself that once kept you safe but now keeps you small.",
    metaTitle: "Why You Feel Stuck Even When You Know What to Do",
    metaDescription:
      "Feeling stuck may not mean you lack clarity. You may be protecting an older version of yourself that once kept you safe but now keeps you small.",
    ogTitle: "Why You Feel Stuck Even When You Know What to Do",
    ogDescription:
      "The block is rarely discipline or opportunity. Sometimes it is loyalty to an older self.",
    primarySeoPhrase: "why do I feel stuck",
    aeoQuestion: "Why do I feel stuck even when I know what to do?",
    themeTags: ["Identity", "Momentum"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "doorway",
      prompt:
        "A dark room with a single old coat hanging beside a glowing open doorway. The coat symbolizes the old identity. The doorway symbolizes the future self. Warm gold light spills from the threshold. Cinematic editorial still life, black and gold, premium magazine feel.",
      alt: "An old coat hanging beside a softly glowing doorway, symbolizing loyalty to an old version of yourself.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "You may feel stuck because part of you is still protecting an identity you have outgrown. The issue is not always discipline, clarity, or opportunity. Sometimes the real block is loyalty to an older version of yourself that once kept you safe but now keeps you small.",
    opening: [
      "You keep calling it confusion because confusion sounds innocent.",
      "But sometimes you are not confused. You are attached.",
      "Attached to the version of you that had an excuse. Attached to the story that made your hesitation understandable. Attached to the identity that lets you delay the move you already know you need to make.",
      "That is not a lack of clarity.",
      "That is emotional loyalty.",
      "And if you are honest, the old version of you has been getting more protection than the future version of you.",
    ],
    bodySections: [
      {
        heading: "The old version had a job",
        paragraphs: [
          "There was probably a time when that version of you made sense. The guarded version. The cautious version. The version that overexplained, overworked, overgave, overthought, or disappeared before anyone could reject you.",
          "That version may have helped you survive something.",
          "But survival patterns do not automatically become life patterns.",
          "A shield is useful in a war. It becomes heavy when you keep carrying it into rooms where no one is attacking you.",
        ],
      },
      {
        heading: "Safety can become sabotage",
        paragraphs: [
          "The part of you that says \u201cnot yet\u201d may not be wise. It may just be familiar.",
          "The part of you that keeps delaying may not be protecting your future. It may be protecting an identity that no longer has authority over where you are going.",
          "This is where people lie to themselves.",
          "They say they need more time. More clarity. More confidence. More signs.",
          "But sometimes what they really need is the courage to stop making the old self the final decision maker.",
        ],
      },
      {
        heading: "You do not need more signs",
        paragraphs: [
          "If you keep circling the same move, the same idea, the same conversation, the same desire, that is information.",
          "You may not need a bigger sign. You may need to stop negotiating with the part of you that benefits from staying unclear.",
          "There is a difference between patience and avoidance. Patience has peace in it. Avoidance has tension. You know the difference.",
        ],
      },
      {
        heading: "The move you keep avoiding is probably the door",
        paragraphs: [
          "The next version of you will not always arrive as motivation.",
          "Sometimes it arrives as discomfort. Sometimes it arrives as a quiet truth that keeps interrupting your excuses. Sometimes it arrives as the one thing you keep avoiding because it would require you to become honest.",
          "Not perfect. Honest.",
        ],
      },
    ],
    closingInvitation:
      "Start with the signal underneath the hesitation. The Clarity Snapshot was built for that first moment of recognition.",
    faqs: [
      {
        q: "Why do I feel stuck even when I know what to do?",
        a: "You may feel stuck because your identity, habits, or emotional patterns are still organized around an older version of yourself. Knowing what to do is not always enough if part of you is still protecting the past.",
      },
      {
        q: "How do I know if I am avoiding growth?",
        a: "Avoidance often feels like repeated delay, overthinking, or searching for certainty when the next step is already clear enough to begin.",
      },
    ],
    relatedSlugs: ["force-door-open-not-your-door", "waiting-for-right-time-fear", "stop-abandoning-who-you-are"],
  },
  {
    id: 2,
    slug: "more-strategy-wrong-signal",
    title: "More Strategy Will Not Save You If Your Signal Is Wrong.",
    excerpt:
      "A strategy can fail when it is built from fear, pressure, comparison, or proving. The signal beneath the strategy matters.",
    metaTitle: "Why Your Strategy Is Not Working",
    metaDescription:
      "A strategy can fail when it is built from fear, pressure, comparison, or proving. The signal beneath the strategy matters.",
    ogTitle: "Why Your Strategy Is Not Working",
    ogDescription:
      "Strategy is an amplifier. It magnifies the signal it is built on. Examine the signal first.",
    primarySeoPhrase: "why strategy is not working",
    aeoQuestion: "Why is my strategy not working?",
    themeTags: ["Strategy", "Signal"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "compass",
      prompt:
        "A beautifully drawn map on a dark table, but the compass beside it points in the wrong direction. Gold light catches the compass needle. Cinematic editorial still life, warm gold accents.",
      alt: "A compass pointing away from a detailed map, symbolizing strategy built from the wrong signal.",
    },
    directAnswerLabel: "The signal",
    directAnswer:
      "A strategy may fail when it is built from the wrong internal signal. If your actions are driven by fear, pressure, comparison, or proving yourself, even a good strategy can create exhaustion instead of momentum.",
    opening: [
      "Some of you are addicted to strategy because it lets you avoid honesty.",
      "You want another framework, another plan, another system, another expert, another app, another calendar, another reset.",
      "But the problem is not always the plan.",
      "The problem is the energy underneath the plan.",
      "A fear-based strategy still smells like fear. A proving-based offer still carries the weight of insecurity. A brand built from comparison still feels like a costume.",
      "You do not need another strategy yet. You need to ask what signal is driving the strategy you already have.",
    ],
    bodySections: [
      {
        heading: "Strategy amplifies the signal beneath it",
        paragraphs: [
          "Strategy is not magic. It is an amplifier.",
          "If the signal is clean, strategy gives it structure.",
          "If the signal is distorted, strategy gives the distortion more reach.",
          "That is why some people can have the perfect plan and still feel like they are dragging themselves through mud. They are not failing because they lack information. They are failing because the plan is being powered by fear.",
        ],
      },
      {
        heading: "Forced action creates distorted results",
        paragraphs: [
          "There is a kind of action that looks productive from the outside but feels violent on the inside.",
          "You are posting, building, networking, planning, sending, pitching, applying, launching, and still something feels off.",
          "That feeling matters. Not because every aligned move is comfortable. But because constant inner resistance is information.",
          "You cannot keep ignoring the body and then wonder why the work feels dead.",
        ],
      },
      {
        heading: "Alignment is not laziness",
        paragraphs: [
          "Some people hear the word alignment and think it means waiting around until life becomes easy. That is not alignment.",
          "Alignment is not the absence of effort. It is the absence of self-betrayal.",
          "Aligned work can still stretch you. It can still challenge you. It can still require discipline. But it does not require you to become someone false in order to move.",
        ],
      },
      {
        heading: "The right strategy feels cleaner",
        paragraphs: [
          "The right strategy does not always feel easy. But it usually feels cleaner.",
          "There is less noise. Less performance. Less emotional debt. Less proving.",
          "You are no longer trying to force an outcome to validate your worth. You are building from a signal that can actually sustain the work.",
        ],
      },
    ],
    closingInvitation:
      "Start with the signal before the strategy. The Snapshot was built to show you what may be driving the moves you keep making.",
    faqs: [
      {
        q: "Why is my strategy not working?",
        a: "Your strategy may not be working because the energy behind it is misaligned. A good plan can still fail if it is driven by fear, comparison, pressure, or proving.",
      },
      {
        q: "Should I change my strategy or my mindset first?",
        a: "Start by examining the signal underneath the strategy. Once the signal is clearer, the right strategic adjustments become easier to see.",
      },
    ],
    relatedSlugs: ["want-clarity-rewarding-chaos", "burned-out-betraying-your-energy", "force-door-open-not-your-door"],
  },
  {
    id: 3,
    slug: "want-clarity-rewarding-chaos",
    title: "You Keep Saying You Want Clarity, But You Keep Rewarding Chaos.",
    excerpt:
      "Clarity requires conditions. If your habits, environment, and commitments keep rewarding chaos, clarity cannot stabilize.",
    metaTitle: "How to Get Clarity When Your Life Feels Chaotic",
    metaDescription:
      "Clarity requires conditions. If your habits, environment, and commitments keep rewarding chaos, clarity cannot stabilize.",
    ogTitle: "How to Get Clarity When Your Life Feels Chaotic",
    ogDescription: "Clarity is an environment, not a thought. It needs space, honesty, repetition.",
    primarySeoPhrase: "how to get clarity in life",
    aeoQuestion: "Why do I keep feeling unclear?",
    themeTags: ["Clarity", "Focus"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "line-through-noise",
      prompt:
        "A desk covered in scattered papers, open tabs reflected in a dark screen, and one clear gold line running through the mess. Editorial, cinematic, premium.",
      alt: "A chaotic desk with a single gold line cutting through scattered papers, symbolizing clarity inside disorder.",
    },
    directAnswerLabel: "The mirror",
    directAnswer:
      "You may keep feeling unclear because your daily habits reward noise. Clarity requires space, honesty, and repeated choices that support direction. If your environment, conversations, and commitments keep reinforcing chaos, clarity cannot stabilize.",
    opening: [
      "You say you want clarity.",
      "But your calendar says chaos. Your phone says distraction. Your relationships say obligation. Your habits say avoidance.",
      "At some point, you have to stop pretending clarity is something that randomly arrives.",
      "Clarity is not a fairy. It does not visit people who keep making their life inhospitable to truth.",
      "If every day is full of noise, the signal does not disappear. You just stop being able to hear it.",
    ],
    bodySections: [
      {
        heading: "Clarity needs conditions",
        paragraphs: [
          "Clarity is not just a thought. It is an environment.",
          "It needs enough space to be heard. Enough honesty to be trusted. Enough repetition to become real.",
          "If your life keeps rewarding panic, distraction, and overcommitment, then clarity will feel like a stranger knocking on a door you never open.",
        ],
      },
      {
        heading: "Chaos can become an identity",
        paragraphs: [
          "Some people are not just surrounded by chaos. They are secretly attached to it.",
          "Chaos gives them an excuse. Chaos makes delay understandable. Chaos lets them say, \u201cI would move if things were calmer.\u201d",
          "But what if calm never comes because chaos has become the way you avoid choosing?",
          "That is not a schedule issue. That is a pattern.",
        ],
      },
      {
        heading: "Confusion can be a shield",
        paragraphs: [
          "Confusion is not always the absence of knowing.",
          "Sometimes confusion is the cover story for a decision you are afraid to make.",
          "If you stay unclear, you do not have to act. If you do not act, you do not have to risk being seen. If you are not seen, you do not have to find out what happens when you stop hiding.",
          "That is why clarity can feel threatening. It removes the hiding place.",
        ],
      },
      {
        heading: "The first clear move is usually simple",
        paragraphs: [
          "People love to make clarity dramatic because drama delays responsibility.",
          "But the first clear move is often painfully simple.",
          "Send the message. End the loop. Write the page. Start the thing. Say no. Tell the truth. Begin again.",
        ],
      },
    ],
    closingInvitation:
      "If the path feels scattered, begin with the Snapshot. Not because it solves everything, but because it gives the signal somewhere to land.",
    faqs: [
      {
        q: "How do I get clarity in life?",
        a: "Create conditions that allow clarity to stabilize. Reduce noise, stop rewarding chaos, and take one honest action that matches what you already know.",
      },
      {
        q: "Why do I keep feeling unclear?",
        a: "You may feel unclear because your habits, relationships, or environment keep reinforcing distraction and avoidance.",
      },
    ],
    relatedSlugs: ["life-you-want-too-loud-to-notice", "stop-asking-for-a-sign", "more-strategy-wrong-signal"],
  },
  {
    id: 4,
    slug: "burned-out-betraying-your-energy",
    title:
      "You Are Not Burned Out Because You Care Too Much. You Are Burned Out Because You Keep Betraying Your Own Energy.",
    excerpt:
      "Burnout is not always about workload. It can also come from misaligned commitments, people-pleasing, and repeated self-betrayal.",
    metaTitle: "Why You Are Burned Out Even When You Care",
    metaDescription:
      "Burnout is not always about workload. It can also come from misaligned commitments, people-pleasing, and repeated self-betrayal.",
    ogTitle: "Why You Are Burned Out Even When You Care",
    ogDescription: "Care without boundaries becomes leakage. Your body keeps the receipts.",
    primarySeoPhrase: "why am I burned out",
    aeoQuestion: "What causes burnout besides working too much?",
    themeTags: ["Energy", "Boundaries"],
    primaryPath: "book",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "candle",
      prompt:
        "A gold candle burning at both ends inside a dark room, with melted wax forming a thin line toward an unopened door. Cinematic, premium, editorial still life.",
      alt: "A candle burning at both ends, symbolizing energy depletion and misaligned effort.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Burnout is not only caused by workload. It can also come from repeatedly betraying your own energy through misaligned commitments, emotional overextension, people-pleasing, and forcing yourself to perform a version of success that does not fit.",
    opening: [
      "Let us be honest.",
      "Some of your burnout is not noble.",
      "Some of it is the receipt for all the times you said yes when your whole body said no.",
      "All the times you made yourself available to people who only know how to take. All the times you confused being needed with being aligned. All the times you called it responsibility because you did not want to admit it was fear.",
      "You are not tired because you care too much. You are tired because your energy has been living without boundaries.",
    ],
    bodySections: [
      {
        heading: "Care without boundaries becomes leakage",
        paragraphs: [
          "Caring is beautiful. But care without boundaries becomes a leak.",
          "It starts quietly. One extra favor. One unnecessary explanation. One more emotional burden you pick up because someone else refuses to carry their own.",
          "Then one day you look up and wonder why you have no energy left for the life you say you want.",
          "The answer is not mysterious. You have been giving your best energy to everything except your own alignment.",
        ],
      },
      {
        heading: "Your body remembers your yeses",
        paragraphs: [
          "Your mind can justify almost anything. Your body is less impressed.",
          "It remembers the meetings you did not want. The conversations that drained you. The obligations that made your chest tighten. The people you kept accommodating long after the relationship became one-sided.",
          "You can call it maturity. Your body may call it betrayal.",
        ],
      },
      {
        heading: "Misalignment is expensive",
        paragraphs: [
          "Misalignment charges interest.",
          "At first, it costs a little energy. Then it costs your focus. Then your creativity. Then your joy. Then your sense of self.",
          "By the time people call it burnout, the body has usually been sending invoices for months.",
        ],
      },
      {
        heading: "Your ambition is not the problem",
        paragraphs: [
          "Ambition is not the enemy. The issue is ambition without energetic integrity.",
          "You can want more. Build more. Serve more. Create more.",
          "But if the path requires you to abandon yourself to sustain it, the success will not feel like success when it arrives.",
        ],
      },
    ],
    closingInvitation:
      "The book opens this deeper conversation through the principle that energy is the currency. If that line lands, keep reading.",
    faqs: [
      {
        q: "What causes burnout besides working too much?",
        a: "Burnout can come from emotional overextension, people-pleasing, misaligned commitments, and repeated self-abandonment.",
      },
      {
        q: "How do I know if I am betraying my energy?",
        a: "Notice where your body consistently contracts, where your yes feels dishonest, and where your commitments drain more than they serve.",
      },
    ],
    relatedSlugs: ["lack-confidence-breaking-self-trust", "force-door-open-not-your-door", "stop-abandoning-who-you-are"],
  },
  {
    id: 5,
    slug: "life-you-want-too-loud-to-notice",
    title: "The Life You Want Is Not Hiding. You Are Just Too Loud to Notice It.",
    excerpt:
      "Direction often becomes visible when noise decreases. The next step may already be present beneath overthinking, comparison, and urgency.",
    metaTitle: "How to Find Direction When You Feel Lost",
    metaDescription:
      "Direction often becomes visible when noise decreases. The next step may already be present beneath overthinking, comparison, and urgency.",
    ogTitle: "How to Find Direction When You Feel Lost",
    ogDescription: "The next step often speaks quietly because it is not performing.",
    primarySeoPhrase: "how to find direction in life",
    aeoQuestion: "How do I find direction when I feel lost?",
    themeTags: ["Direction", "Stillness"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "road",
      prompt:
        "A quiet road under a night sky with a faint gold signal line glowing along the path. No people. Minimal and cinematic. Premium editorial.",
      alt: "A quiet road with a faint gold line leading forward, symbolizing direction becoming visible through stillness.",
    },
    directAnswerLabel: "What this really means",
    directAnswer:
      "Direction often becomes visible when you stop overwhelming yourself with noise. The next step may not be hidden. It may already be present, but buried beneath overthinking, comparison, urgency, and emotional clutter.",
    opening: [
      "The answer is not always missing. Sometimes you are just too loud.",
      "Too many opinions. Too many tabs open. Too many people in your ear. Too many versions of yourself trying to win approval from audiences that do not even matter.",
      "And then you wonder why you cannot hear your own life.",
      "You keep asking for direction while drowning the signal in noise. That is not searching. That is self-interference.",
    ],
    bodySections: [
      {
        heading: "Your direction needs silence",
        paragraphs: [
          "Not forever. Not isolation. Not disappearing from the world.",
          "Just enough silence to hear what keeps trying to rise underneath the noise.",
          "The next step often speaks quietly because it is not performing. It does not beg for attention. It waits for you to stop giving your entire nervous system to distraction.",
        ],
      },
      {
        heading: "Noise can feel productive",
        paragraphs: [
          "This is the trap. Noise often looks responsible.",
          "Researching. Planning. Asking everyone. Checking more options. Watching one more video. Reading one more thread.",
          "But after a certain point, information becomes avoidance with better branding. You are not gathering insight anymore. You are delaying contact with the truth.",
        ],
      },
      {
        heading: "The honest answer is often subtle",
        paragraphs: [
          "The direction that is actually yours may not arrive like fireworks.",
          "It may arrive as a small pull. A repeated curiosity. A quiet envy. A topic you keep returning to. A conversation that wakes something up. A problem you cannot stop wanting to solve.",
          "Stop dismissing the signal because it is not screaming.",
        ],
      },
      {
        heading: "Your life cannot answer through everyone else",
        paragraphs: [
          "Other people can reflect. They can support. They can ask better questions.",
          "But they cannot become the authority over your inner yes.",
          "At some point, you have to stop outsourcing the part of the decision only your life can make.",
        ],
      },
    ],
    closingInvitation: "The Snapshot is a mirror before the map. If you feel scattered, start there.",
    faqs: [
      {
        q: "How do I find direction when I feel lost?",
        a: "Reduce the noise around you and notice what keeps returning with energy, honesty, or quiet persistence. Direction often appears when you stop drowning it out.",
      },
      {
        q: "Why do I overthink my next step?",
        a: "Overthinking can be a way to avoid responsibility, fear, or uncertainty. It can also come from relying too heavily on outside opinions.",
      },
    ],
    relatedSlugs: ["want-clarity-rewarding-chaos", "stop-asking-for-a-sign", "waiting-for-right-time-fear"],
  },
  {
    id: 6,
    slug: "force-door-open-not-your-door",
    title: "If You Have to Force the Door Open, It Might Not Be Your Door.",
    excerpt:
      "Effort and force are not the same. If something repeatedly drains your energy or requires self-betrayal, it may not be your door.",
    metaTitle: "How to Know If You Are Forcing Something",
    metaDescription:
      "Effort and force are not the same. If something repeatedly drains your energy or requires self-betrayal, it may not be your door.",
    ogTitle: "How to Know If You Are Forcing Something",
    ogDescription: "Aligned effort can stretch you. Forced effort distorts you.",
    primarySeoPhrase: "forcing things in life",
    aeoQuestion: "How do I know if I am forcing something?",
    themeTags: ["Resistance", "Alignment"],
    primaryPath: "blueprint",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "doorway-alt",
      prompt:
        "A closed black door with gold light leaking from a different open doorway nearby. One handprint is visible on the closed door, but no person is shown. Editorial, cinematic, premium.",
      alt: "A closed door with gold light shining from another doorway nearby, symbolizing the difference between force and alignment.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "You may be forcing something when the action repeatedly drains your energy, requires you to abandon your values, or depends on becoming someone you are not. Effort is normal. Constant self-betrayal is not.",
    opening: [
      "Not every closed door is a test of your persistence.",
      "Some doors are closed because they are not yours.",
      "But your ego does not like that. Your ego wants to be chosen. It wants to prove it can break through. It wants to turn resistance into a personality trait.",
      "So you keep pushing. Even though your body is tired. Even though your joy is gone. Even though the opportunity requires a version of you that feels like a costume.",
      "That is not ambition. That is attachment wearing a motivational hoodie.",
    ],
    bodySections: [
      {
        heading: "Effort and force are not the same",
        paragraphs: [
          "Aligned effort can stretch you. Forced effort distorts you.",
          "Aligned effort asks you to grow. Forced effort asks you to betray yourself.",
          "Aligned effort may be challenging, but it does not require you to keep abandoning your own signal to continue. That difference matters.",
        ],
      },
      {
        heading: "Resistance gives information",
        paragraphs: [
          "Resistance is not always a wall. Sometimes it is a warning. Sometimes it is a teacher. Sometimes it is just fear standing in front of growth.",
          "Your work is to learn the difference.",
          "If resistance contracts your spirit and requires self-betrayal, listen closely. If resistance feels vulnerable but expansive, you may be at the edge of growth.",
        ],
      },
      {
        heading: "The ego loves hard things for the wrong reason",
        paragraphs: [
          "The ego can become addicted to impossible doors. Not because they are aligned, but because they create a stage.",
          "Look how hard I am trying. Look how much I can endure. Look how badly I want it.",
          "But endurance is not proof of alignment. Sometimes it is proof you are still negotiating with a wound.",
        ],
      },
      {
        heading: "The aligned door may not flatter your old identity",
        paragraphs: [
          "This is why people miss it.",
          "The aligned door may be simpler. Quieter. Less impressive to the part of you that wants applause.",
          "It may not validate the identity you built to survive. It may call forward someone more honest.",
        ],
      },
    ],
    closingInvitation:
      "Start the Personal Action Plan when you are ready to name what you have been forcing and what your energy is actually pointing toward.",
    faqs: [
      {
        q: "How do I know if I am forcing something?",
        a: "You may be forcing something if it consistently drains you, requires you to abandon your values, or depends on pretending to be someone you are not.",
      },
      {
        q: "Is resistance always a sign to stop?",
        a: "No. Some resistance protects you from misalignment. Some resistance appears when you are approaching growth. The difference is usually felt in whether the resistance contracts or expands you.",
      },
    ],
    relatedSlugs: ["not-stuck-loyal-to-old-version", "burned-out-betraying-your-energy", "waiting-for-right-time-fear"],
  },
  {
    id: 7,
    slug: "lack-confidence-breaking-self-trust",
    title: "Your Problem Is Not That You Lack Confidence. It Is That You Keep Breaking Trust With Yourself.",
    excerpt:
      "Confidence is often weakened by broken self-trust. Rebuild confidence by aligning your actions with your own knowing.",
    metaTitle: "Why You Lack Confidence and How Self-Trust Rebuilds It",
    metaDescription:
      "Confidence is often weakened by broken self-trust. Rebuild confidence by aligning your actions with your own knowing.",
    ogTitle: "Why You Lack Confidence and How Self-Trust Rebuilds It",
    ogDescription: "Confidence is a trust account. Every aligned action makes a deposit.",
    primarySeoPhrase: "how to build confidence",
    aeoQuestion: "Why do I lack confidence?",
    themeTags: ["Confidence", "Self-Trust"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "kintsugi",
      prompt:
        "A cracked gold mirror being repaired with thin gold seams, inspired by kintsugi, reflecting a soft light instead of a face. Editorial, cinematic, premium.",
      alt: "A cracked mirror repaired with gold seams, symbolizing self-trust and confidence being rebuilt.",
    },
    directAnswerLabel: "The mirror",
    directAnswer:
      "Confidence is often weakened by broken self-trust. When you repeatedly ignore your instincts, abandon your boundaries, or fail to honor your own commitments, your nervous system stops believing your words.",
    opening: [
      "You do not build confidence by yelling affirmations at a life you keep betraying.",
      "You build confidence by becoming someone your own spirit can trust.",
      "Every time you say yes when you mean no, you weaken trust. Every time you promise yourself a change and then negotiate it away, you weaken trust. Every time you ignore the inner signal because someone else\u2019s approval feels safer, you weaken trust.",
      "And then you call the result insecurity.",
      "No. It is not always insecurity. Sometimes it is evidence.",
    ],
    bodySections: [
      {
        heading: "Confidence is a trust account",
        paragraphs: [
          "Confidence is not a personality trait reserved for louder people. It is a relationship with yourself.",
          "Every aligned action makes a deposit. Every act of self-betrayal makes a withdrawal.",
          "If you keep overdrafting your own integrity, no affirmation is going to save you. Your system wants proof.",
        ],
      },
      {
        heading: "Your body remembers broken promises",
        paragraphs: [
          "You may forget the promise you made to yourself on Monday. Your body does not.",
          "It remembers the boundary you ignored. The project you abandoned. The truth you softened to keep someone comfortable. The dream you keep treating like a hobby because taking it seriously would make you responsible.",
          "That is why confidence cannot be faked for long.",
        ],
      },
      {
        heading: "Small integrity rebuilds the signal",
        paragraphs: [
          "The way back is not dramatic. It is small.",
          "Tell the truth faster. Keep one promise. Say no once. Finish the thing. Leave when your body knows. Start before you feel impressive.",
          "Confidence returns when your actions start matching your knowing.",
        ],
      },
      {
        heading: "You do not need to feel ready to act",
        paragraphs: [
          "Readiness is often a reward, not a prerequisite.",
          "You act with enough honesty. Then trust returns. You take the next aligned step. Then confidence has evidence.",
          "You stop abandoning yourself in small places. Then the larger move becomes possible.",
        ],
      },
    ],
    closingInvitation: "Take the Clarity Snapshot and locate where your signal is asking for repair.",
    faqs: [
      {
        q: "Why do I lack confidence?",
        a: "You may lack confidence because you have repeatedly broken trust with yourself. Confidence grows when your actions begin matching your inner knowing.",
      },
      {
        q: "How do I build confidence?",
        a: "Build confidence by keeping small promises, honoring boundaries, and taking actions that prove to yourself that your words can be trusted.",
      },
    ],
    relatedSlugs: ["burned-out-betraying-your-energy", "stop-abandoning-who-you-are", "waiting-for-right-time-fear"],
  },
  {
    id: 8,
    slug: "stop-asking-for-a-sign",
    title: "Stop Asking for a Sign When You Keep Ignoring the Ones That Cost You Something.",
    excerpt:
      "You may keep asking for signs because the real ones require responsibility. Guidance means little if you refuse movement.",
    metaTitle: "Why You Keep Asking for Signs",
    metaDescription:
      "You may keep asking for signs because the real ones require responsibility. Guidance means little if you refuse movement.",
    ogTitle: "Why You Keep Asking for Signs",
    ogDescription: "The real signal asks for responsibility. Repetition is information.",
    primarySeoPhrase: "looking for signs in life",
    aeoQuestion: "Why do I keep asking for signs?",
    themeTags: ["Signal", "Accountability"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "constellation",
      prompt:
        "A dark wall covered in small gold marks and symbols, with one bright sign pointing toward an open path. The mood is mysterious but grounded. Editorial, premium.",
      alt: "Gold signs on a dark wall pointing toward an open path, symbolizing guidance that requires action.",
    },
    directAnswerLabel: "The signal",
    directAnswer:
      "People often keep asking for signs when they want certainty without responsibility. A real sign may require action, discomfort, or a decision. If you only accept signs that do not cost you anything, you may be avoiding the truth.",
    opening: [
      "You do not need another sign. You need to stop pretending the last five did not count.",
      "The discomfort counted. The repeated pattern counted. The door that kept draining you counted. The conversation that showed you who someone was counted. The idea that keeps returning when everything is quiet counted.",
      "But you keep asking for a sign because you want one that does not require courage.",
      "That is not spiritual. That is avoidance with incense burning in the background.",
    ],
    bodySections: [
      {
        heading: "Signs are not always comforting",
        paragraphs: [
          "A real sign does not always arrive wrapped in peace.",
          "Sometimes it arrives as discomfort. Sometimes it arrives as repetition. Sometimes it arrives as the same lesson wearing different clothes.",
          "Sometimes the sign is not telling you what to do. It is showing you what you can no longer deny.",
        ],
      },
      {
        heading: "The real signal asks for responsibility",
        paragraphs: [
          "This is where people get selective.",
          "They want guidance as long as it does not ask for a boundary. They want confirmation as long as it does not require a conversation. They want clarity as long as it does not cost an identity.",
          "But the signal is not here to decorate your avoidance. It is here to move you.",
        ],
      },
      {
        heading: "Repetition is information",
        paragraphs: [
          "If the same pattern keeps returning, stop acting shocked. That is data.",
          "The same kind of person. The same kind of delay. The same kind of exhaustion. The same kind of opportunity you dismiss. The same idea that will not leave.",
          "At some point, repetition becomes a form of mercy. Life keeps circling the truth because you keep walking past it.",
        ],
      },
      {
        heading: "Guidance without movement becomes noise",
        paragraphs: [
          "If you keep receiving insight but refusing action, eventually even guidance becomes clutter.",
          "Not because it stopped being true. Because you stopped honoring it.",
          "The question is not whether life is speaking. The question is whether you are willing to move when the message is inconvenient.",
        ],
      },
    ],
    closingInvitation:
      "Do not leave with the same question you arrived with. Begin with the Snapshot and let the first signal become visible.",
    faqs: [
      {
        q: "Why do I keep asking for signs?",
        a: "You may keep asking for signs because you want certainty without responsibility. Sometimes the real sign is already present, but it requires action.",
      },
      {
        q: "How do I know if something is a sign?",
        a: "Look for repetition, emotional charge, energetic clarity, and patterns that keep returning even when you try to ignore them.",
      },
    ],
    relatedSlugs: ["want-clarity-rewarding-chaos", "life-you-want-too-loud-to-notice", "waiting-for-right-time-fear"],
  },
  {
    id: 9,
    slug: "waiting-for-right-time-fear",
    title: "You Are Not Waiting for the Right Time. You Are Waiting for a Version of Yourself That Does Not Feel Fear.",
    excerpt:
      "Waiting for the right time can become a way to avoid fear. Momentum often begins before certainty arrives.",
    metaTitle: "Why Waiting for the Right Time Keeps You Stuck",
    metaDescription:
      "Waiting for the right time can become a way to avoid fear. Momentum often begins before certainty arrives.",
    ogTitle: "Why Waiting for the Right Time Keeps You Stuck",
    ogDescription: "Fear does not mean stop. Certainty is not the entrance fee.",
    primarySeoPhrase: "waiting for the right time",
    aeoQuestion: "Why do I keep waiting for the right time?",
    themeTags: ["Timing", "Fear"],
    primaryPath: "blueprint",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "clock",
      prompt:
        "A gold clock without hands sitting on a dark table, with sunrise light beginning behind it. Editorial, cinematic, calm, premium.",
      alt: "A clock without hands beside a faint sunrise, symbolizing waiting for the perfect time.",
    },
    directAnswerLabel: "What this really means",
    directAnswer:
      "Waiting for the right time can become a way to avoid fear. The right time does not always feel fearless. Often, momentum begins when the next step feels honest, slightly uncomfortable, and aligned enough to take.",
    opening: [
      "The right time is a beautiful lie when you use it to avoid becoming responsible.",
      "You say you are waiting for timing. But you are really waiting for fear to disappear.",
      "You are waiting for certainty to walk in, clean your room, fix your confidence, organize your plan, and guarantee that nobody will judge you.",
      "That version of the moment is not coming.",
      "The real next step will probably still have fear in the room. The difference is, fear will no longer be in charge.",
    ],
    bodySections: [
      {
        heading: "Timing can become a hiding place",
        paragraphs: [
          "Timing matters. But hiding behind timing is different.",
          "You know when you are being patient. You also know when you are delaying. Patience has trust in it. Delay has bargaining.",
          "If every season becomes \u201cnot yet,\u201d the problem may not be timing. It may be your relationship with visibility, risk, or responsibility.",
        ],
      },
      {
        heading: "Fear does not mean stop",
        paragraphs: [
          "Fear is not always a warning. Sometimes fear is the nervous system reacting to unfamiliar expansion.",
          "You can feel fear and still be aligned. You can feel fear and still be ready. You can feel fear and still take the next clean step.",
          "Stop making fear the final authority over your life.",
        ],
      },
      {
        heading: "Certainty is not the entrance fee",
        paragraphs: [
          "Some people never begin because they are waiting for certainty to pay the cost of entry. But certainty often comes after movement.",
          "You step. You learn. You adjust. You gather evidence. You build trust.",
          "You do not think your way into every version of readiness. Sometimes you move your way there.",
        ],
      },
      {
        heading: "The next step does not need to be dramatic",
        paragraphs: [
          "You do not have to blow up your life. You do not have to announce a rebirth. You do not have to become a different person by Monday.",
          "You just need to take the next honest step and stop pretending you need a perfect emotional climate to begin.",
        ],
      },
    ],
    closingInvitation: "Turn reflection into direction. The Personal Action Plan is where the signal becomes structure.",
    faqs: [
      {
        q: "Why do I keep waiting for the right time?",
        a: "You may be waiting for the right time because you are hoping fear will disappear before you act. Many aligned steps still include fear.",
      },
      {
        q: "How do I know when it is time to start?",
        a: "It may be time to start when the next step feels honest, repeated, slightly uncomfortable, and aligned enough to take.",
      },
    ],
    relatedSlugs: ["not-stuck-loyal-to-old-version", "force-door-open-not-your-door", "lack-confidence-breaking-self-trust"],
  },
  {
    id: 10,
    slug: "stop-abandoning-who-you-are",
    title: "You Do Not Need to Become Someone Else. You Need to Stop Abandoning Who You Already Are.",
    excerpt:
      "Authenticity is often less about adding a new identity and more about removing the patterns that taught you to abandon yourself.",
    metaTitle: "How to Be Yourself Without Becoming Someone Else",
    metaDescription:
      "Authenticity is often less about adding a new identity and more about removing the patterns that taught you to abandon yourself.",
    ogTitle: "How to Be Yourself Without Becoming Someone Else",
    ogDescription: "Alignment feels like recognition. The return is quieter than the performance.",
    primarySeoPhrase: "how to be yourself",
    aeoQuestion: "How do I become my authentic self?",
    themeTags: ["Authenticity", "Identity"],
    primaryPath: "blueprint",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "mirror-chair",
      prompt:
        "A dark mirror with a gold outline, reflecting an empty chair lit by warm light. The image should feel like returning to yourself. Editorial, cinematic, premium.",
      alt: "A gold-framed mirror reflecting an empty chair in warm light, symbolizing returning to the authentic self.",
    },
    directAnswerLabel: "The mirror",
    directAnswer:
      "Becoming your authentic self is often less about adding a new identity and more about removing the patterns that made you abandon yourself. Authenticity returns when your choices, energy, relationships, and work begin matching what you already know to be true.",
    opening: [
      "The self-improvement industry convinced too many people that transformation means becoming a more impressive stranger.",
      "New habits. New identity. New aesthetic. New language. New personality. New performance.",
      "But what if the real work is not becoming someone else? What if the real work is noticing all the places you learned to leave yourself?",
      "The room where you got quiet. The relationship where you got smaller. The ambition that was never yours. The version of success that made you feel less alive.",
      "You do not need to become more fake in a more polished way. You need to come home.",
    ],
    bodySections: [
      {
        heading: "Authenticity is not a brand costume",
        paragraphs: [
          "Authenticity is not an aesthetic. It is not a color palette. It is not a content style. It is not performing vulnerability so people clap for your honesty.",
          "Authenticity is congruence. It is what happens when your choices stop contradicting your knowing.",
        ],
      },
      {
        heading: "You learned to abandon yourself for belonging",
        paragraphs: [
          "Most people did not abandon themselves randomly. They learned it.",
          "They learned which parts got approval. Which parts made people uncomfortable. Which truths created conflict. Which desires seemed inconvenient.",
          "So they adjusted. Then adjusted again. Then one day they called the performance a personality.",
        ],
      },
      {
        heading: "Alignment feels like recognition",
        paragraphs: [
          "When something is aligned, it may not feel flashy. It may feel familiar in a deeper way.",
          "Like recognition. Like relief. Like the body exhaling. Like something in you saying, \u201cThere you are.\u201d",
          "That feeling matters. It may be quieter than excitement, but it is often more trustworthy.",
        ],
      },
      {
        heading: "The return is quieter than the performance",
        paragraphs: [
          "Coming home to yourself does not always look impressive from the outside.",
          "Sometimes it looks like leaving. Sometimes it looks like starting small. Sometimes it looks like telling the truth without explaining it to everyone. Sometimes it looks like not auditioning anymore.",
          "That may not impress the room. But it may save your life force.",
        ],
      },
    ],
    closingInvitation:
      "The book opens the framework. The Personal Action Plan helps you apply it to your own life, language, and next aligned move.",
    faqs: [
      {
        q: "How do I become my authentic self?",
        a: "Becoming authentic often means removing the habits, relationships, and performances that taught you to abandon yourself.",
      },
      {
        q: "What does authenticity feel like?",
        a: "Authenticity often feels like recognition, relief, clarity, and congruence between what you know, what you choose, and how you move.",
      },
    ],
    relatedSlugs: ["not-stuck-loyal-to-old-version", "lack-confidence-breaking-self-trust", "burned-out-betraying-your-energy"],
  },
  // ---------------------------------------------------------------------
  // Batch 1 of the Journal Content Expansion. Blunt, empathetic OSIRIS
  // tone. Every entry follows the same scaffolding: short truth, mirror,
  // pattern, questions worth asking, pathway invitation. NO em-dash or
  // en-dash characters anywhere in this batch (style requirement).
  // ---------------------------------------------------------------------
  {
    id: 11,
    slug: "ego-sounds-like-protection",
    title: "Your Ego Does Not Always Sound Like Pride. Sometimes It Sounds Like Protection.",
    excerpt:
      "The version of ego most people miss is the one that calls itself caution. Not pride. Not arrogance. Protection.",
    metaTitle: "Ego Disguised as Protection: A Hidden Pattern",
    metaDescription:
      "Ego does not always look like arrogance. Often it sounds like caution, hesitation, or protection. Here is how to recognize it.",
    ogTitle: "When Ego Sounds Like Protection",
    ogDescription:
      "The version of ego that hides as caution is the one that quietly runs your life.",
    primarySeoPhrase: "ego disguised as protection",
    aeoQuestion: "How does ego disguise itself as protection?",
    themeTags: ["Identity", "Fear", "Self-Trust"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "shield",
      prompt:
        "A polished gold shield resting on a dark velvet surface, gentle warm light from one side casting a long shadow. The shield is slightly tarnished where it has been held the longest. Cinematic editorial still life, premium magazine feel, black and gold tonal palette.",
      alt: "A polished gold shield on dark velvet, symbolizing the ego that calls itself protection.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Ego is not only the loud version of yourself that demands credit. It is also the quiet voice that calls itself caution, the part of you that protects an image of who you think you should be. That softer ego is harder to see and harder to dismantle.",
    opening: [
      "You think you have done the work on your ego because you do not brag.",
      "You think you have humility because you can list your flaws.",
      "But the ego is not only the version of you that wants applause.",
      "It is also the version of you that wants to stay safe from being misread.",
      "The version that hesitates because being seen incorrectly feels worse than being seen at all.",
      "That is still ego. Just better dressed.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "The loud ego wants to be admired. The quiet ego wants to be unreachable.",
          "It tells you to wait, to refine the idea one more time, to keep the post in drafts, to not respond yet, to not commit yet, to not show up yet. It sounds like discernment. It feels like maturity. It is often just self protection wearing better vocabulary.",
          "The mirror is uncomfortable here because most of the language you use to defend the delay was language you earned in real seasons of growth. The same words can be sincere in one chapter and self preserving in the next.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Notice where you keep using thoughtful language to justify avoidance.",
          "You may say you are protecting your peace when you are actually protecting your image. You may say you are waiting for clarity when you are actually waiting for a guarantee. You may say you are being intentional when you are really being scared in a controlled way.",
          "None of this makes you a bad person. It just means the next layer of your work is the layer most people never get to. The layer where you stop letting protection be the highest virtue in your life.",
        ],
      },
      {
        heading: "What changes when you see it",
        paragraphs: [
          "Once you can name the protective ego, you stop arguing with the part of you that benefits from staying hidden. You begin to act before the protection feels comfortable. You begin to take the small visible step you keep avoiding.",
          "The point is not to override yourself. The point is to stop calling fear by a holier name.",
        ],
      },
    ],
    closingInvitation:
      "If the protective version of you has been making most of your decisions, start by reading the signal underneath. The Clarity Snapshot is a short and honest mirror.",
    faqs: [
      {
        q: "How does ego disguise itself as protection?",
        a: "Ego can show up as caution, perfectionism, waiting for the right time, or refusing to be misread. These behaviors look mature but often protect an image of yourself you are afraid to lose.",
      },
      {
        q: "Is all caution ego?",
        a: "No. Real discernment has peace in it. Protective ego has tension and repetition. If you keep circling the same decision while calling it patience, that is often ego in disguise.",
      },
    ],
    relatedSlugs: ["not-stuck-loyal-to-old-version", "stop-abandoning-who-you-are", "force-door-open-not-your-door"],
  },
  {
    id: 12,
    slug: "standards-or-fear",
    title: "You Keep Calling It Standards, But It Might Be Fear With Better Vocabulary.",
    excerpt:
      "High standards can be genuine. They can also be a polished way to keep yourself unreachable. The difference matters.",
    metaTitle: "High Standards or Hidden Fear?",
    metaDescription:
      "High standards can become a quiet way to avoid commitment. Learn the difference between discernment and self protection.",
    ogTitle: "Standards or Fear: The Difference Most People Miss",
    ogDescription:
      "Real standards keep you aligned. Disguised fear keeps you alone and unstarted.",
    primarySeoPhrase: "high standards or fear",
    aeoQuestion: "How do I know if my standards are real or fear?",
    themeTags: ["Boundaries", "Fear", "Self-Trust"],
    primaryPath: "consultation",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "lock",
      prompt:
        "A heavy gold lock resting on a black marble surface beside an open invitation envelope. The lock is unused and pristine. The envelope is unopened. Warm sidelight. Cinematic editorial still life, black and gold tonal palette.",
      alt: "An unused gold lock beside an unopened invitation envelope, symbolizing standards that prevent connection.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Real standards have peace in them. They organize your life around what you actually value. Disguised fear sounds like standards but keeps producing the same outcome: nothing fully begins, no one fully gets in, nothing fully arrives. If your standards always end with you alone in the same place, they may be fear in a better outfit.",
    opening: [
      "You say you have standards.",
      "But the result has been quiet.",
      "No project chosen. No partner trusted. No risk taken. No real thing built.",
      "And the explanation is always the same. The opportunity was not good enough. The person was not exactly right. The timing was not perfect.",
      "At some point the pattern stops being evidence of taste.",
      "At some point it starts looking like a beautifully worded refusal to be touched.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "Standards that are real protect a yes. They make sure the things that get in are aligned with the life you are actually building.",
          "Standards that are fear protect from a no. They make sure nothing gets close enough to disappoint you, to demand something of you, to ask you to commit to a version of yourself that could fail.",
          "The first one creates a clear life. The second one creates a quiet one.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Notice the frequency of your rejections versus the frequency of your investments.",
          "If you reject far more than you build, far more than you choose, far more than you risk, that is a signal. Not a moral failing. A signal.",
          "The cost of disguised fear is that you keep meeting the next opportunity with the same filter that rejected the last one, and you call the empty calendar discernment.",
        ],
      },
      {
        heading: "What real standards do",
        paragraphs: [
          "Real standards point you toward what you would risk for. They give a yes its weight by making the no honest.",
          "If your standards have never produced a yes that mattered, the standard may not be the problem. The fear underneath the standard may be.",
        ],
      },
    ],
    closingInvitation:
      "If the same filter keeps producing the same emptiness, name the frame. The Clarity Reset Session is one focused conversation to see what your standards have been quietly protecting.",
    faqs: [
      {
        q: "How do I know if my standards are real or fear?",
        a: "Real standards eventually produce a yes you can name. Fear in standards clothing produces a long pattern of no without an alternative ever arriving.",
      },
      {
        q: "Should I lower my standards?",
        a: "Not necessarily. Examine whether they are organizing your life or insulating it. The fix is rarely lower, the fix is usually honest.",
      },
    ],
    relatedSlugs: ["force-door-open-not-your-door", "not-stuck-loyal-to-old-version", "lack-confidence-breaking-self-trust"],
  },
  {
    id: 13,
    slug: "afraid-to-start-small",
    title: "You Are Not Above the Work. You Are Afraid to Be Seen Starting Small.",
    excerpt:
      "The block is rarely the work. It is the visibility of the early version of yourself doing the work badly.",
    metaTitle: "Why You Are Afraid to Start Small",
    metaDescription:
      "Most people who say they are above the work are afraid of being seen at the beginning. The fix is not motivation. It is visibility tolerance.",
    ogTitle: "You Are Not Above the Work",
    ogDescription:
      "The early version of you doing it imperfectly is the version that earns the later version of you doing it well.",
    primarySeoPhrase: "afraid to start small",
    aeoQuestion: "Why am I afraid to start small?",
    themeTags: ["Resistance", "Authenticity", "Momentum"],
    primaryPath: "blueprint",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "seed",
      prompt:
        "A single small gold seedling pushing through dark soil in a clay pot, with one bright spotlight from above. The pot is plain and slightly imperfect. Cinematic editorial still life, black and gold tonal palette, premium magazine composition.",
      alt: "A small gold seedling pushing through soil in a plain clay pot, lit by a single spotlight.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "You are not blocked by the size of the next move. You are blocked by who would see you make it small. The fear is not the work. The fear is being witnessed at the beginning, before the work makes you look like the version of yourself you want to be.",
    opening: [
      "You keep imagining the launch, the studio, the platform, the room, the version of yourself who has already arrived.",
      "And then you compare every small starting step to that future image and decide it is beneath you.",
      "So nothing begins.",
      "Not because you are lazy.",
      "Because the early version of the work is a public confession that you are not yet the person you want to be.",
      "And being seen at that gap feels worse than not starting at all.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "There is a private agreement most people have with themselves. They will begin once they can begin impressively.",
          "But the work does not respect that agreement. The work asks you to be visible at the level you are at right now and to keep showing up while the level changes.",
          "Anyone who has built anything real has gone through the season where the work was worse than their taste. That gap is not a failure. It is the cost of entry.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Watch how often you delay the small visible step because you are waiting to start at the level of the version of you who has already mastered it.",
          "That is not standards. That is performance protection. You are not above the work. You are afraid of being witnessed becoming.",
          "And the irony is that the people you respect most went through exactly this. You just met them later.",
        ],
      },
      {
        heading: "What changes when you accept the early version",
        paragraphs: [
          "Begin the small thing while being honest that it is early. Name it. Show up anyway. The visibility tolerance is the muscle, not the polish.",
          "What you build from honesty travels further than what you build from image protection. The early version of you doing it imperfectly is the version that earns the later version of you doing it well.",
        ],
      },
    ],
    closingInvitation:
      "If the small visible step is the one you keep avoiding, the Personal Action Plan can give it structure. A guided intake that turns instinct into a starting move you can actually make.",
    faqs: [
      {
        q: "Why am I afraid to start small?",
        a: "You are usually afraid of being seen at a level that does not match the image you want others to hold of you. The fear is not the work. It is the witnessed gap between where you are and where you want to be.",
      },
      {
        q: "How do I get over the fear of starting small?",
        a: "Begin the visible step before you feel ready. Build tolerance for being witnessed at an early stage. The fluency comes from repetition, not from waiting.",
      },
    ],
    relatedSlugs: ["force-door-open-not-your-door", "not-stuck-loyal-to-old-version", "waiting-for-right-time-fear"],
  },
  {
    id: 14,
    slug: "defending-the-wound",
    title: "You Say You Want Growth, But You Keep Defending the Wound.",
    excerpt:
      "Growth requires the wound to lose its job. Most people will not let it. The wound has been useful too long.",
    metaTitle: "Why You Defend the Wound Even When You Want Growth",
    metaDescription:
      "Growth requires the wound to lose its job. Many people protect the wound because it has shaped their identity. Here is what to do about it.",
    ogTitle: "Defending the Wound: The Quiet Block to Growth",
    ogDescription:
      "The wound has been useful too long. Growth begins when you stop arguing on its behalf.",
    primarySeoPhrase: "defending the wound",
    aeoQuestion: "Why do I defend the wound even when I want growth?",
    themeTags: ["Identity", "Self-Trust", "Authenticity"],
    primaryPath: "consultation",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "armor",
      prompt:
        "A piece of intricately engraved gold armor displayed on a black mannequin stand, lit gently from the side. The armor has been polished often but never removed. Cinematic editorial still life, premium magazine feel.",
      alt: "A piece of engraved gold armor on a black stand, lit from the side, symbolizing a wound kept polished.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Growth asks the wound to lose its central role in your story. Many people quietly refuse because the wound has organized their identity, their relationships, their language, and their justifications for too long. You are not failing to grow. You are protecting the part of you that the growth would replace.",
    opening: [
      "You say you want to change.",
      "But every conversation circles back to the same evidence of why you cannot.",
      "The story that explains your hesitation. The chapter that explains your distance. The person who let you down. The pattern you keep naming but never finishing.",
      "It is not that the wound is not real.",
      "It is that the wound has been useful for a long time.",
      "And growth would require it to be retired.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "The wound has earned its place. It has explained the years you do not want to defend out loud. It has held the line for you in rooms where you did not have the words.",
          "But there is a quiet moment where it stops being a memory you survived and starts being a contract you keep renewing.",
          "Growth requires the renewal to end. That is the part most people refuse without ever naming the refusal.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Notice how often the wound shows up to justify the small no. The boundary that was actually avoidance. The conversation you would not have. The risk you would not take. The closeness you would not allow.",
          "If every potential growth move gets vetoed by the same memory, the memory is not a witness. It is a gatekeeper. And you put it there.",
          "Naming this is not a betrayal of your past. It is a return of authority to your future.",
        ],
      },
      {
        heading: "What it looks like to retire it",
        paragraphs: [
          "You do not pretend it did not happen. You stop letting it be the highest authority in the room.",
          "You begin to let new evidence be evidence. You stop running every future decision through the same old filter. The wound becomes part of your story instead of the entire ceiling of it.",
        ],
      },
    ],
    closingInvitation:
      "If the same wound keeps vetoing every growth move, the Clarity Reset Session is built to name the frame and return the decision to you. One focused 30 minute session.",
    faqs: [
      {
        q: "Why do I defend the wound even when I want growth?",
        a: "Because the wound has organized your identity and explanations for a long time. Growth requires that organizing role to end, and many people quietly refuse without ever naming the refusal.",
      },
      {
        q: "How do I stop defending the wound?",
        a: "Name where it shows up to veto growth moves. Let new evidence count. Allow the wound to become part of the story instead of the deciding voice in every new chapter.",
      },
    ],
    relatedSlugs: ["not-stuck-loyal-to-old-version", "ego-sounds-like-protection", "stop-abandoning-who-you-are"],
  },
  {
    id: 15,
    slug: "addicted-to-potential",
    title: "You Are Addicted to Potential Because Completion Would Expose You.",
    excerpt:
      "Potential is safe. Completion has a verdict. Most people who live in potential are protecting themselves from the result.",
    metaTitle: "Addicted to Potential: Why You Avoid Completion",
    metaDescription:
      "Living in potential protects you from a verdict. Completion makes the work readable. Here is how to move from potential to finished.",
    ogTitle: "You Are Addicted to Potential",
    ogDescription:
      "Potential is safe. Completion exposes you. The work changes when you choose finished over impressive.",
    primarySeoPhrase: "addicted to potential",
    aeoQuestion: "Why am I addicted to my potential?",
    themeTags: ["Resistance", "Identity", "Momentum"],
    primaryPath: "blueprint",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "draft",
      prompt:
        "A stack of beautifully bound but unfinished gold edged journals on a dark walnut desk, each with a ribbon marking the same early page. Soft directional light. Cinematic editorial still life, premium magazine feel.",
      alt: "A stack of unfinished gold edged journals on a dark walnut desk, each marked at an early page.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Potential is socially safe. It lets people imagine the version of you that has already succeeded. Completion is honest. It puts a real result on the table and ends the imagination. Most people who live in potential are quietly protecting themselves from a verdict they cannot control.",
    opening: [
      "You are very impressive on paper.",
      "You are very promising in conversation.",
      "You are very almost.",
      "And the part of you that loves being almost is the part of you that has not yet had to be measured.",
      "Potential lets the room project. Completion makes the room respond.",
      "And responding is a risk.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "When the work is unfinished, no one can disagree with it. When it is finished, it can be ignored, misunderstood, or worse, accurately critiqued.",
          "The unfinished version of you keeps everyone polite and keeps every door theoretically open. That theoretical openness is what makes the unfinished life so addictive.",
          "It feels patient and protective. It is often just unwilling to be witnessed at a verdict.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Notice the projects you start beautifully and abandon at eighty percent.",
          "Notice the ideas you describe brilliantly in private and never put in front of anyone.",
          "Notice the offers you almost launched, the books you almost wrote, the moves you almost made.",
          "The pattern is not laziness. It is a relationship with the version of you that never has to be evaluated. That version is the one you keep protecting.",
        ],
      },
      {
        heading: "What completion teaches",
        paragraphs: [
          "Finishing one small thing while imperfect is more valuable than maintaining the illusion of ten brilliant unfinished things.",
          "Completion is the only honest data your future self has to work with. Without it, every decision is theoretical and every plan is rehearsal.",
          "The fix is not bigger ambition. It is smaller commitments you actually close.",
        ],
      },
    ],
    closingInvitation:
      "If the cycle of brilliant beginnings and quiet abandonments keeps repeating, the Personal Action Plan can give one of them structure that ends in a finished move you can name.",
    faqs: [
      {
        q: "Why am I addicted to my potential?",
        a: "Because potential keeps you safe from a verdict. As long as the work is unfinished, no one can fully respond to it. Completion ends the imagination and starts the evaluation, and that vulnerability is what most people are quietly avoiding.",
      },
      {
        q: "How do I stop living in potential?",
        a: "Choose one small thing and close it. Then another. Completion is a muscle. It is built through small honest finishes, not through bigger plans.",
      },
    ],
    relatedSlugs: ["afraid-to-start-small", "force-door-open-not-your-door", "not-stuck-loyal-to-old-version"],
  },
  // ---------------------------------------------------------------------
  // Batch 2 of the Journal Content Expansion. Same OSIRIS scaffolding,
  // same hard rule: NO em-dash or en-dash characters anywhere in this
  // batch. `avoiding-grief-of-growth` is the new featured post.
  // ---------------------------------------------------------------------
  {
    id: 16,
    slug: "control-is-not-alignment",
    title: "Control Is Not Alignment. It Is Often Fear Wearing a Strategy Suit.",
    excerpt:
      "Control feels like clarity from the inside. From the outside it looks like a person who refuses to be moved by anything they did not plan.",
    metaTitle: "Control Is Not Alignment: How They Differ",
    metaDescription:
      "Control and alignment can look similar from the outside. One creates a clear life. The other creates a quiet one. Here is the difference.",
    ogTitle: "Control Is Not Alignment",
    ogDescription:
      "Alignment moves with the room. Control rules over it. The difference shapes everything you build.",
    primarySeoPhrase: "control vs alignment",
    aeoQuestion: "What is the difference between control and alignment?",
    themeTags: ["Alignment", "Fear", "Identity"],
    primaryPath: "book",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "compass",
      prompt:
        "A vintage gold brass compass on a dark slate surface beside a black leather strap, soft top light, with a slight tarnish on the rim. Cinematic editorial still life, black and gold tonal palette, premium magazine feel.",
      alt: "A vintage gold brass compass beside a black leather strap, lit softly from above.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Alignment moves with what is true even when the room shifts. Control tries to rule the room so the truth never has to change. From the inside, both feel like strategy. Only one of them lets the work breathe.",
    opening: [
      "You have always been the person who plans well.",
      "You think in systems. You see the next step before the room is ready for it.",
      "But the part of you that loves control is not the same part of you that loves alignment.",
      "Control is the version of you that needs the outcome to be predictable.",
      "Alignment is the version of you that can move with what is actually happening.",
      "They sound similar. They are not.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "Control is what you reach for when you cannot tolerate being surprised.",
          "It looks like preparation. It looks like discipline. It looks like leadership.",
          "But underneath it is often a refusal to be moved by anything you did not author yourself. That refusal is what stops the work from becoming alive. Living work breathes. Controlled work calcifies.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Notice the moments where the plan stops serving you and you keep serving the plan.",
          "Notice where you call rigidity discipline. Notice where you call resistance to feedback vision. Notice where you call inflexibility integrity.",
          "Each of those is a place where control has quietly replaced alignment, and the cost is that the work stops listening to the very signal it was built from.",
        ],
      },
      {
        heading: "What alignment lets in",
        paragraphs: [
          "Alignment will sometimes look like changing your mind in public.",
          "It will sometimes look like dropping the plan you spent weeks defending. It will sometimes look like trusting the signal more than the system you built around the signal.",
          "Alignment is not weakness. It is the only stance honest enough to keep producing work that is alive.",
        ],
      },
    ],
    closingInvitation:
      "If the difference between control and alignment is what your work has been waiting for, the book gives you the framework that names it cleanly.",
    faqs: [
      {
        q: "What is the difference between control and alignment?",
        a: "Alignment can change shape with new information without losing the signal underneath. Control resists the new information to protect the existing plan, even when the plan is the thing that needs to move.",
      },
      {
        q: "How do I know if I am leading from control?",
        a: "Notice how you respond to surprise. If new data threatens you more than it informs you, you are likely leading from control rather than alignment.",
      },
    ],
    relatedSlugs: ["force-door-open-not-your-door", "not-stuck-loyal-to-old-version", "ego-sounds-like-protection"],
  },
  {
    id: 17,
    slug: "truth-or-comfort",
    title: "You Do Not Want the Truth. You Want a Version That Lets You Stay Comfortable.",
    excerpt:
      "Most people are not searching for clarity. They are negotiating for a version of the truth that does not ask anything of them.",
    metaTitle: "Why You Want Comfort, Not Truth",
    metaDescription:
      "Truth changes the calendar. Comfort keeps it the same. Most people are negotiating for a version of clarity that does not cost anything.",
    ogTitle: "Truth or Comfort: The Quiet Trade",
    ogDescription:
      "Real clarity asks for a move. Comfortable clarity asks for nothing. Notice which one you usually choose.",
    primarySeoPhrase: "truth or comfort",
    aeoQuestion: "Why do I avoid the truth when I say I want clarity?",
    themeTags: ["Clarity", "Resistance", "Self-Trust"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "mirror",
      prompt:
        "A large gold framed antique mirror partially covered by a soft black velvet drape, lit by warm directional light, faint dust catching in a single beam. Cinematic editorial still life, premium magazine composition.",
      alt: "A gold framed antique mirror partially covered by a black velvet drape.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Truth is not a feeling. It is a shape your life has to take once you stop arguing with it. Most people who say they want clarity are looking for a softer version of the truth that lets them keep the calendar they already have. That softer version is not clarity. It is comfort with a better wardrobe.",
    opening: [
      "You ask the question.",
      "You read the books. You sit with the journal. You light the candle. You meditate.",
      "And then the answer comes.",
      "And it asks you to change something specific.",
      "And you suddenly need more information.",
      "That moment is not curiosity. That is the negotiation.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "Real clarity is recognizable because it is uncomfortable in a very particular way.",
          "It does not feel cinematic. It does not feel sacred. It feels like one specific action you now know you have been avoiding.",
          "Comfortable clarity, by contrast, feels meaningful in a way that does not require a single change. That is the tell.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Watch what you do with answers that ask for movement.",
          "Most people add another book, another conversation, another framework on top of the clear answer they already have, hoping a slightly more poetic version of the truth will arrive that does not require the move.",
          "It will not. The truth has already been delivered. The negotiation is what keeps it from landing.",
        ],
      },
      {
        heading: "What it costs to stay comfortable",
        paragraphs: [
          "Every year you stay in the negotiation, you build a version of yourself that has more language and less movement.",
          "You become impressive at describing your stuckness. The friction stops being a place you pass through and starts becoming a place you live.",
          "Comfort is not bad. Mistaking it for clarity is.",
        ],
      },
    ],
    closingInvitation:
      "If you suspect you have been negotiating instead of listening, the Clarity Snapshot is a short and honest mirror. It does not tell you what to do. It tells you where you are.",
    faqs: [
      {
        q: "Why do I avoid the truth when I say I want clarity?",
        a: "Because the truth almost always asks for one specific move you have been avoiding. Asking for clarity feels safe. Receiving it does not. Most people pause at the threshold and call it more research.",
      },
      {
        q: "How do I tell truth from comfort?",
        a: "Notice whether the realization asks you to change something concrete. Comfort produces understanding that fits the life you already have. Truth produces a move you have not made yet.",
      },
    ],
    relatedSlugs: ["ego-sounds-like-protection", "defending-the-wound", "force-door-open-not-your-door"],
  },
  {
    id: 18,
    slug: "finding-yourself-without-performance",
    title: "You Keep Trying to Find Yourself Without Losing the Performance.",
    excerpt:
      "You can not become someone new while protecting the role that produced the old one. The performance is the cost of admission to the next version.",
    metaTitle: "Finding Yourself Without Losing the Performance",
    metaDescription:
      "Most people want a new self with the old applause. Becoming requires the performance to fall away. Here is what that actually means.",
    ogTitle: "Finding Yourself Without Performance",
    ogDescription:
      "The performance got you here. It will not get you the next chapter.",
    primarySeoPhrase: "finding yourself without performance",
    aeoQuestion: "How do I find myself without losing my role?",
    themeTags: ["Identity", "Authenticity", "Resistance"],
    primaryPath: "blueprint",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "mask",
      prompt:
        "A theatrical gold ceremonial mask resting on a black silk pillow inside an empty velvet lined case, with a single shaft of warm light from above. Cinematic editorial still life, premium magazine composition.",
      alt: "A gold ceremonial mask on a black silk pillow inside an empty velvet case.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "You can not become someone new while still being praised for being the old one. The performance is what made the room comfortable with you. It is also what is keeping you from the next version. The two cannot share the same chair.",
    opening: [
      "You have been the steady one.",
      "Or the impressive one. Or the helpful one. Or the calm one. Or the spiritual one. Or the high performing one.",
      "And the role works.",
      "It is what people trust. It is what got you here.",
      "And now you are quietly trying to become someone slightly different.",
      "But you do not want to lose the room you built with the old role.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "Becoming is not free. Every new version costs the applause of the old one.",
          "The people who chose you for the role will not always choose you for the next iteration. The systems that organized your value will have to be reorganized. The way you are introduced in rooms will have to change.",
          "Most people will not pay that cost. They want the new self with the old approval still in place.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Watch where you reach for the old role exactly at the moment a new version of you is trying to land.",
          "You quote the credentials. You return to the language that won the last room. You explain the change in terms the audience that knew the previous you can still process.",
          "Each of those reaches is a small refusal of becoming. Done enough times, the performance survives. The becoming does not.",
        ],
      },
      {
        heading: "What it actually takes",
        paragraphs: [
          "Let some of the old applause go before it is replaced.",
          "You will not have the new room yet. That is part of the cost. The middle is supposed to feel quiet. The quiet is what tells you the performance is finally losing its grip.",
          "Build the new shape into a structure rather than negotiating it inside the old one.",
        ],
      },
    ],
    closingInvitation:
      "If the next version of you needs a shape it can stand inside, the Personal Action Plan is a guided intake that turns instinct into a structure you can name and act on.",
    faqs: [
      {
        q: "How do I find myself without losing my role?",
        a: "You usually cannot. Becoming asks for the old role to lose centrality. The fix is not to find a version that keeps both. The fix is to let the new shape be smaller and more honest until it is ready to lead.",
      },
      {
        q: "What is the cost of dropping the performance?",
        a: "A season of quiet. Some of the old room will not follow you. That cost is what makes the new room possible.",
      },
    ],
    relatedSlugs: ["not-stuck-loyal-to-old-version", "stop-abandoning-who-you-are", "afraid-to-start-small"],
  },
  {
    id: 19,
    slug: "sabotaging-the-wrong-plan",
    title: "The Part of You That Keeps Sabotaging the Plan Might Be the Part That Knows the Plan Is Wrong.",
    excerpt:
      "What you call self sabotage may be the most honest part of you refusing to keep building something that was never yours.",
    metaTitle: "Self Sabotage Might Be Honesty",
    metaDescription:
      "When the same plan keeps failing in the same way, the saboteur may not be broken. It may be the part of you that knows the plan is wrong.",
    ogTitle: "Sabotaging the Wrong Plan",
    ogDescription:
      "What looks like self sabotage is often the most honest signal you have.",
    primarySeoPhrase: "self sabotage wrong plan",
    aeoQuestion: "Why do I keep sabotaging the same plan?",
    themeTags: ["Self-Trust", "Alignment", "Authenticity"],
    primaryPath: "consultation",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "blueprint",
      prompt:
        "A folded architectural blueprint with gold ink lines partly torn at the corner, lying on a dark walnut table beside a brass straight edge. Cinematic editorial still life, premium magazine composition.",
      alt: "A folded gold inked blueprint partly torn at the corner, lying on a dark walnut table.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "What you call self sabotage is sometimes self honesty in a costume. The part of you that keeps undermining the plan may not be broken. It may be the part of you that knows the plan was assembled from someone else's voice and refuses to let you finish building a life that was never yours.",
    opening: [
      "You have been trying to execute this plan for years.",
      "You start. You build. You almost get there. And then something happens. You disappear. You drop the ball. You start fights. You miss the deadline. You sabotage the relationship. You undo the launch.",
      "And you call it self sabotage.",
      "But what if it is not sabotage.",
      "What if it is the most honest version of you refusing to complete a structure you did not actually want to live inside.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "The saboteur usually arrives at the same point in the cycle.",
          "Right before completion. Right before commitment. Right before visibility. Right before becoming responsible for the outcome of the path you said you wanted.",
          "If the breakdown always happens at the same edge, the question is not how to stop the breakdown. The question is what the breakdown is telling you about the edge.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Many of the plans we sabotage were not built from our own signal.",
          "They were inherited from a previous season, borrowed from a mentor, copied from someone we admired, or shaped to satisfy a room we no longer belong in.",
          "When the foundation is not yours, the part of you that knows it will eventually refuse to keep building. Calling that refusal sabotage is a way to avoid asking the harder question. Whose plan was this in the first place.",
        ],
      },
      {
        heading: "What changes when you listen",
        paragraphs: [
          "Stop treating the saboteur as the enemy and start treating it as a signal.",
          "What is it refusing to complete. What does it stop you from finalizing. What direction does it close off. The answer is often where the actual plan was supposed to begin.",
          "Sometimes the most honest thing you can do is admit the plan was wrong and let the new one form from quieter ground.",
        ],
      },
    ],
    closingInvitation:
      "If the same plan keeps collapsing in the same way, the saboteur is not the problem. The frame is. The Clarity Reset Session is built to name the frame and return the decision to you.",
    faqs: [
      {
        q: "Why do I keep sabotaging the same plan?",
        a: "Often because the plan was not built from your own signal. The part of you that knows refuses to finish building a structure you did not actually choose to live inside.",
      },
      {
        q: "How do I tell sabotage from honest refusal?",
        a: "Sabotage feels chaotic and ashamed. Honest refusal feels quiet, clear, and consistent. If the same breakdown happens at the same edge every time, listen to the edge.",
      },
    ],
    relatedSlugs: ["defending-the-wound", "not-stuck-loyal-to-old-version", "addicted-to-potential"],
  },
  {
    id: 20,
    slug: "avoiding-grief-of-growth",
    title: "You Are Not Confused. You Are Avoiding the Grief of Outgrowing What Once Fit.",
    excerpt:
      "Confusion is often the politest word we have for grief. The version of your life you have outgrown is asking you to mourn it before you move.",
    metaTitle: "The Grief of Outgrowing What Once Fit",
    metaDescription:
      "What you call confusion is often unprocessed grief. Outgrowing a chapter requires you to mourn it before the next one can fully begin.",
    ogTitle: "Avoiding the Grief of Growth",
    ogDescription:
      "Confusion is often grief in a softer voice. The next chapter requires the last one to be mourned, not edited.",
    primarySeoPhrase: "grief of outgrowing",
    aeoQuestion: "Why do I feel confused instead of clear when I outgrow something?",
    themeTags: ["Identity", "Clarity", "Authenticity"],
    primaryPath: "consultation",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_AT,
    updatedAt: UPDATED_AT,
    featuredImage: {
      motif: "candle",
      prompt:
        "A single tall gold candle just blown out, faint smoke curling, set on a black ceramic plate against a deep shadowed background. Cinematic editorial still life, premium magazine feel.",
      alt: "A gold candle just blown out with faint smoke rising on a black ceramic plate.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "You are not confused. You are mourning. Outgrowing a chapter that once held you is a small private death, and most people do not have the language for it, so they call it confusion. Confusion sounds like a question. Grief asks for time. Calling it the wrong name keeps you stuck inside it.",
    opening: [
      "You used to know what you wanted.",
      "Now you cannot describe it cleanly. The old answers do not arrive when you ask the old questions.",
      "And you keep waiting for clarity to return.",
      "It will not. Not in the form you remember.",
      "Because what is actually happening is not a clarity problem.",
      "It is a grief problem you have been calling confusion to keep it manageable.",
    ],
    bodySections: [
      {
        heading: "The mirror",
        paragraphs: [
          "Grief is what we feel when a version of our life ends, even when the ending is good for us.",
          "Outgrowing a job, a relationship, a city, a community, a self image, a creative direction, all of it asks us to mourn what once fit.",
          "And most people do not mourn it. They explain it, edit it, or try to skip it. The unmourned chapter then sits inside the body as confusion, which is just grief refusing to be named.",
        ],
      },
      {
        heading: "The pattern",
        paragraphs: [
          "Notice the conversations you keep having with yourself that go nowhere.",
          "The looping thought that you should have figured this out by now. The frustration that an answer is not arriving. The quiet exhaustion of trying to think your way into a feeling you have not allowed.",
          "That loop is rarely a thinking problem. It is grief asking for room you have not given it.",
        ],
      },
      {
        heading: "What the grief is for",
        paragraphs: [
          "Grief is the body's way of formally ending one chapter so a new one can begin.",
          "Skipping it does not save time. It extends the in between, sometimes for years. Allowing it does not weaken you. It returns you to ground.",
          "The next clear move usually arrives within a few months of letting the prior chapter actually be over.",
        ],
      },
    ],
    closingInvitation:
      "If the in between has stretched longer than it should, the Clarity Reset Session is built to name the frame, honor what has ended, and return the next aligned move to you.",
    faqs: [
      {
        q: "Why do I feel confused instead of clear when I outgrow something?",
        a: "Because clarity asks for a move and grief asks for time. When a chapter has ended but has not been mourned, the body produces confusion as a way to slow the transition until the loss is acknowledged.",
      },
      {
        q: "How do I move through the grief of growth?",
        a: "Name what has ended in plain language. Let the chapter formally close inside you. Allow the next direction to arrive without forcing it. The grief is the gate, not the obstacle.",
      },
    ],
    relatedSlugs: ["not-stuck-loyal-to-old-version", "defending-the-wound", "stop-abandoning-who-you-are"],
  },
];

export const FEATURED_SLUG = "waiting-room-is-not-wasted-time";

export const FILTER_THEMES = [
  "Identity",
  "Clarity",
  "Transition",
  "Discipline",
  "Healing",
  "Relationships",
  "Momentum",
  "Reflection",
  "Purpose",
  "Inner Work",
];

export const getPostBySlug = (slug) =>
  JOURNAL_POSTS.find((p) => p.slug === slug) || null;

export const getRelatedPosts = (slug, max = 3) => {
  const post = getPostBySlug(slug);
  if (!post) return [];
  return (post.relatedSlugs || [])
    .map(getPostBySlug)
    .filter(Boolean)
    .slice(0, max);
};

export const getFeaturedPost = () => getPostBySlug(FEATURED_SLUG) || JOURNAL_POSTS[0];

export const SITE_URL = "https://authentic-momentum.com";

export const AUTHOR = {
  "@type": "Person",
  name: "Cyrus Prescod",
  url: `${SITE_URL}/about`,
  jobTitle: "Author, Strategist, Builder",
};

export const ORGANIZATION = {
  "@type": "Organization",
  name: "Authentic Momentum",
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
};
